import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flashcardAPI } from '../services/api';
import MatchGame from '../components/MatchGame';

const FlashcardMatch = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [flashcard, setFlashcard] = useState(null);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameTerms, setGameTerms] = useState(null);
  const [showSelection, setShowSelection] = useState(false);

  useEffect(() => {
    loadFlashcard();
  }, [id]);

  const loadFlashcard = async () => {
    try {
      setLoading(true);
      const response = await flashcardAPI.getFlashcard(id);
      setFlashcard(response.data.flashCard);
      setTerms(response.data.terms);
      
      // If more than 10 terms, show selection screen, otherwise start directly
      if (response.data.terms.length > 10) {
        setShowSelection(true);
      } else {
        setGameTerms(response.data.terms);
      }
      
      setError('');
    } catch (err) {
      setError('Failed to load flashcard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTerms = (useRandom) => {
    if (useRandom) {
      // Randomly select 10 terms
      const shuffled = [...terms].sort(() => Math.random() - 0.5);
      setGameTerms(shuffled.slice(0, 10));
    } else {
      // Use all terms
      setGameTerms(terms);
    }
    setShowSelection(false);
  };

  const handleExit = () => {
    navigate('/flashcards');
  };

  if (loading) {
    return <div className="loading">Loading match game...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <p style={{ color: '#f56565', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={handleExit}
            className="btn btn-secondary"
          >
            ← Back to Flashcards
          </button>
        </div>
      </div>
    );
  }

  if (!flashcard || terms.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <p>No terms found in this flashcard.</p>
          <button 
            onClick={handleExit}
            className="btn btn-secondary"
          >
            ← Back to Flashcards
          </button>
        </div>
      </div>
    );
  }

  // Show selection screen if more than 10 terms
  if (showSelection) {
    return (
      <div className="container">
        <div className="card" style={{ 
          maxWidth: '600px', 
          margin: '40px auto',
          padding: '32px',
          textAlign: 'center',
          backgroundColor: 'white'
        }}>
          <h2 style={{ 
            color: '#667eea', 
            fontSize: '24px', 
            marginBottom: '12px' 
          }}>
            🎮 Choose Game Mode
          </h2>
          <p style={{ 
            color: '#718096', 
            fontSize: '15px', 
            marginBottom: '32px',
            lineHeight: '1.6'
          }}>
            This flashcard has {terms.length} terms. How would you like to play?
          </p>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => handleSelectTerms(true)}
              style={{
                padding: '20px 24px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5568d3';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#667eea';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '20px' }}>⚡</span>
              <span>Quick Match (10 Random Terms)</span>
              <span style={{ fontSize: '13px', opacity: 0.9, fontWeight: 'normal' }}>
                Perfect for a quick practice session
              </span>
            </button>

            <button
              onClick={() => handleSelectTerms(false)}
              style={{
                padding: '20px 24px',
                backgroundColor: '#38b2ac',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#319795';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(56, 178, 172, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#38b2ac';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '20px' }}>🎯</span>
              <span>Full Match (All {terms.length} Terms)</span>
              <span style={{ fontSize: '13px', opacity: 0.9, fontWeight: 'normal' }}>
                Challenge yourself with the complete set
              </span>
            </button>
          </div>

          <button
            onClick={handleExit}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: '#718096',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e0';
              e.currentTarget.style.color = '#4a5568';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.color = '#718096';
            }}
          >
            ← Back to Flashcards
          </button>
        </div>
      </div>
    );
  }

  // Only render MatchGame after terms are selected
  if (!gameTerms) {
    return null;
  }

  return (
    <MatchGame 
      flashcard={flashcard}
      terms={gameTerms}
      onExit={handleExit}
    />
  );
};

export default FlashcardMatch;
